import { Button, Col, Descriptions, Row, Table } from 'antd';
import { floor } from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { useReactToPrint } from 'react-to-print';
import styled from 'styled-components';
import { BidProposal } from '../../modules/bid-chances';
import { contractType1Enum } from '../../modules/contracts';
import { priceToString } from '../../util/utils';
import SOVReportComponent from '../common/SOVReportComponent';
import { styledSOVTable } from '../styled/mixin';

const StyledDiv = styled.div`
  ${styledSOVTable}
`;

interface BidProposalDetailComponentProps {
  bidProposal: BidProposal;
  projectName: string;
}

function BidProposalDetailComponent({
  bidProposal,
  projectName,
}: BidProposalDetailComponentProps) {
  let columns = [
    {
      title: '코드',
      dataIndex: 'code',
      width: '10%',
    },
    {
      title: '품명',
      dataIndex: 'name',
      width: '18%',
    },
    {
      title: '규격',
      dataIndex: 'standard',
      width: '13%',
    },
    {
      title: '단위',
      dataIndex: 'unit',
      width: '5%',
    },
    {
      title: '수량',
      dataIndex: 'quantity',
      width: '8%',
      render: (value: any, record: any, index: number) =>
        record.editable ? priceToString(value) : null,
    },
    {
      title: '재료비',
      children: [
        {
          title: '단가',
          dataIndex: 'materialCost',
          width: '8%',
          render: (value: any, record: any, index: number) =>
            record.editable ? priceToString(value) : null,
        },
        {
          title: '합계',
          dataIndex: 'materialCostTotal',
          width: '8%',
          render: (value: any, record: any, index: number) =>
            record.editable ? priceToString(value) : null,
        },
      ],
    },
    {
      title: '노무비',
      children: [
        {
          title: '단가',
          dataIndex: 'laborCost',
          width: '8%',
          render: (value: any, record: any, index: number) =>
            record.editable ? priceToString(value) : null,
        },
        {
          title: '합계',
          dataIndex: 'laborCostTotal',
          width: '8%',
          render: (value: any, record: any, index: number) =>
            record.editable ? priceToString(value) : null,
        },
      ],
    },
    {
      title: '경비',
      children: [
        {
          title: '단가',
          dataIndex: 'expense',
          width: '8%',
          render: (value: any, record: any, index: number) =>
            record.editable ? priceToString(value) : null,
        },
        {
          title: '합계',
          dataIndex: 'expenseTotal',
          width: '8%',
          render: (value: any, record: any, index: number) =>
            record.editable ? priceToString(value) : null,
        },
      ],
    },
    {
      title: '총계',
      dataIndex: 'total',
      width: '8%',
      render: (value: any, record: any, index: number) =>
        record.editable ? priceToString(value) : null,
    },
  ];

  const [tableData, setTableData] = useState<any[]>();
  const [tableTotal, setTableTotal] = useState<number>();

  const componentRef = useRef(null);
  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
  });

  useEffect(() => {
    const newData: any = [];
    for (const lineItem of bidProposal.bidProposalLineItems) {
      const data = {
        id: lineItem['id'],
        code: lineItem['bidPackageLineItem']['code'],
        name: lineItem['bidPackageLineItem']['name'],
        level: lineItem['bidPackageLineItem']['level'],
        standard: lineItem['bidPackageLineItem']['standard'],
        unit: lineItem['bidPackageLineItem']['unit'],
        quantity: lineItem['bidPackageLineItem']['quantity'],
        materialCost: lineItem['materialCost'],
        laborCost: lineItem['laborCost'],
        expense: lineItem['expense'],
        editable: lineItem['bidPackageLineItem']['editable'],
      };
      newData.push(data);
    }

    for (let index = 0; index < newData.length; index++) {
      calcTotal(newData, index);
    }

    setTableTotal(
      newData.reduce(function (pre: any, next: any) {
        return pre + next.total;
      }, 0),
    );

    setTableData(newData);
  }, [bidProposal]);

  const calcTotal = (data: any, index: any) => {
    const _quantity = !!data[index]['quantity'] ? data[index]['quantity'] : 0;
    const _materialCost = !!data[index]['materialCost']
      ? data[index]['materialCost']
      : 0;
    const _laborCost = !!data[index]['laborCost']
      ? data[index]['laborCost']
      : 0;
    const _expense = !!data[index]['expense'] ? data[index]['expense'] : 0;
    data[index]['materialCostTotal'] = floor(_materialCost * _quantity);
    data[index]['laborCostTotal'] = floor(_laborCost * _quantity);
    data[index]['expenseTotal'] = floor(_expense * _quantity);
    data[index]['total'] =
      data[index]['materialCostTotal'] +
      data[index]['laborCostTotal'] +
      data[index]['expenseTotal'];
    data[index]['totalUnitCost'] =
      data[index]['materialCost'] +
      data[index]['laborCost'] +
      data[index]['expense'];
  };
  return (
    <StyledDiv>
      <Row justify="space-between">
        <Col>
          <h2>내역서 상세</h2>
        </Col>
        <Col>
          <Button onClick={handlePrint}>인쇄</Button>
        </Col>
      </Row>
      <Row>
        <Col flex={1}>
          <h3>계약 정보</h3>
        </Col>
      </Row>
      <Descriptions bordered column={2} size="small">
        <Descriptions.Item label="간접노무비 (%)">
          {bidProposal.metaData.costAccountingSheetRate0}
        </Descriptions.Item>

        <Descriptions.Item label="산재보험료 (%)">
          {bidProposal.metaData.costAccountingSheetRate1}
        </Descriptions.Item>

        <Descriptions.Item label="고용보험료 (%)">
          {bidProposal.metaData.costAccountingSheetRate2}
        </Descriptions.Item>

        <Descriptions.Item label="국민건강보험료 (%)">
          {bidProposal.metaData.costAccountingSheetRate3}
        </Descriptions.Item>

        <Descriptions.Item label="국민연금보험료 (%)">
          {bidProposal.metaData.costAccountingSheetRate4}
        </Descriptions.Item>

        <Descriptions.Item label="노인장기요양보험료 (%)">
          {bidProposal.metaData.costAccountingSheetRate5}
        </Descriptions.Item>

        <Descriptions.Item label="퇴직공제부금비 (%)">
          {bidProposal.metaData.costAccountingSheetRate6}
        </Descriptions.Item>

        <Descriptions.Item label="산업안전보건관리비 (%)">
          {bidProposal.metaData.costAccountingSheetRate7}
        </Descriptions.Item>

        <Descriptions.Item label="기타 경비 (%)">
          {bidProposal.metaData.costAccountingSheetRate8}
        </Descriptions.Item>

        <Descriptions.Item label="환경보전비 (%)">
          {bidProposal.metaData.costAccountingSheetRate9}
        </Descriptions.Item>

        <Descriptions.Item label="건설하도급대금지급보증서발급수수료 (%)">
          {bidProposal.metaData.costAccountingSheetRate10}
        </Descriptions.Item>

        <Descriptions.Item label="건설기계대여금지급보증서발급수수료 (%)">
          {bidProposal.metaData.costAccountingSheetRate11}
        </Descriptions.Item>

        <Descriptions.Item label="일반관리비 (%)">
          {bidProposal.metaData.costAccountingSheetRate12}
        </Descriptions.Item>

        <Descriptions.Item label="이윤 (%)">
          {bidProposal.metaData.costAccountingSheetRate13}
        </Descriptions.Item>

        <Descriptions.Item label="부가가치세 (%)">
          {bidProposal.metaData.costAccountingSheetRate14}
        </Descriptions.Item>

        <Descriptions.Item label="매입세 (%)">
          {bidProposal.metaData.costAccountingSheetRate15}
        </Descriptions.Item>
      </Descriptions>

      <Row style={{ marginTop: '20px' }}>
        <Col flex={1}>
          <h3>세부 내역</h3>
        </Col>
      </Row>
      <Row>
        <Col flex={1}>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={tableData}
            scroll={{ y: 500 }}
            pagination={false}
            footer={() => (
              <Row justify="end">
                {'Total: ' +
                  (tableTotal === undefined ? 0 : priceToString(tableTotal!))}
              </Row>
            )}
          />
        </Col>
      </Row>
      {tableData && bidProposal && (
        <div style={{ display: 'none' }}>
          <SOVReportComponent
            projectName={projectName}
            data={tableData}
            contractType1={contractType1Enum.SUBCONTRACT_WORK}
            contract={bidProposal}
            ref={componentRef}
          />
        </div>
      )}
    </StyledDiv>
  );
}

export default BidProposalDetailComponent;
